// DistrictAdmin.tsx
import { useEffect, useState } from 'react';
import { Tabs, Tab, Form, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Table from '../../../core/common/dataTable/index';
import { RootState } from '../../../core/data/redux/store';
import {
    setBlocks,
    setVillages,
    setLoading,
    setError,
    deleteBlock,
    deleteVillages,
} from '../../../core/data/redux/stateDistrictSlice';
import {
    fetchBlocks,
    fetchVillages,
    deleteBlock as deleteBlockService,
    deleteVillage as deleteVillageService,
} from '../../../core/data/services/stateDistrictBlockService';
import { Block, Village } from '../../../core/data/types/stateDistrict';
import BlockForm from './blockForm';
import VillageForm from './villageForm';
import { getUserDetails } from '../../../lib/getUserDetails';

const DistrictAdmin = () => {
    const dispatch = useDispatch();
    const { blocks, villages, loading } = useSelector((state: RootState) => state.stateDistrict);

    const districtAdminDetails = getUserDetails()
    const stateId = districtAdminDetails[0]?.relatedEntity?.districts?.stateId;
    const districtId = districtAdminDetails[0]?.relatedEntity?.districts?.id;



    const userStateId = stateId; 
    const userDistrictId = districtId;

    // State management
    const [selectedState] = useState<number>(userStateId); // Locked to user's state
    const [selectedDistrict] = useState<number>(userDistrictId); // Locked to user's district
    const [selectedBlock, setSelectedBlock] = useState<number | undefined>(undefined);
    const [selectedVillage, setSelectedVillage] = useState<number | undefined>(undefined);
    const [activeTab, setActiveTab] = useState<string>('blocks'); // Default to blocks tab
    const [showBlockForm, setShowBlockForm] = useState(false);
    const [showVillageForm, setShowVillageForm] = useState(false);
    const [editBlock, setEditBlock] = useState<Block | undefined>(undefined);
    const [editVillage, setEditVillage] = useState<Village | undefined>(undefined);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteItem, setDeleteItem] = useState<{ id: number; type: 'block' | 'village' } | null>(null);

    // Fetch blocks for the user's district on mount
    useEffect(() => {
        const loadBlocks = async () => {
            dispatch(setLoading(true));
            try {
                const response = await fetchBlocks(selectedState, selectedDistrict);
                dispatch(setBlocks(response));
            } catch (err: any) {
                dispatch(setError(err.message || 'Failed to fetch blocks'));
                dispatch(setBlocks([]));
            } finally {
                dispatch(setLoading(false));
            }
        };
        loadBlocks();
    }, [selectedState, selectedDistrict, dispatch]);

    // Fetch villages when a block is selected
    useEffect(() => {
        const loadVillages = async () => {
            if (selectedBlock) {
                dispatch(setLoading(true));
                try {
                    const response = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
                    dispatch(setVillages(response));
                } catch (err: any) {
                    dispatch(setError(err.message || 'Failed to fetch villages'));
                    dispatch(setVillages([]));
                } finally {
                    dispatch(setLoading(false));
                }
            } else {
                dispatch(setVillages([]));
                setSelectedVillage(undefined);
            }
        };
        loadVillages();
    }, [selectedState, selectedDistrict, selectedBlock, dispatch]);

    // Table columns with action buttons
    const blockColumns = [
        { title: 'ID', dataIndex: 'id', sorter: (a: Block, b: Block) => a.id - b.id },
        { title: 'Name', dataIndex: 'name', sorter: (a: Block, b: Block) => a.name.localeCompare(b.name) },
        { title: 'District ID', dataIndex: 'districtId', sorter: (a: Block, b: Block) => a.districtId - b.districtId },
        {
            title: 'Actions',
            render: (record: Block) => (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            setEditBlock(record);
                            setShowBlockForm(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                            setDeleteItem({ id: record.id, type: 'block' });
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    const villageColumns = [
        { title: 'ID', dataIndex: 'id', sorter: (a: Village, b: Village) => a.id - b.id },
        { title: 'Name', dataIndex: 'name', sorter: (a: Village, b: Village) => a.name.localeCompare(b.name) },
        { title: 'Block ID', dataIndex: 'blockAreaId', sorter: (a: Village, b: Village) => a.blockAreaId - b.blockAreaId },
        {
            title: 'Actions',
            render: (record: Village) => (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            setEditVillage(record);
                            setShowVillageForm(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                            setDeleteItem({ id: record.id, type: 'village' });
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    // Filter data based on selections and active tab
    const filteredBlocks = Array.isArray(blocks)
        ? selectedBlock && activeTab === 'blocks'
            ? blocks.filter((block) => block.id === selectedBlock && block.districtId === selectedDistrict)
            : blocks.filter((block) => block.districtId === selectedDistrict)
        : [];
    const filteredVillages = Array.isArray(villages)
        ? selectedVillage && activeTab === 'villages'
            ? villages.filter((village) => village.id === selectedVillage && village.blockAreaId === selectedBlock)
            : selectedBlock
                ? villages.filter((village) => village.blockAreaId === selectedBlock)
                : villages
        : [];

    const handleDelete = async () => {
        if (!deleteItem) return;
        try {
            dispatch(setLoading(true));
            if (deleteItem.type === 'block') {
                await deleteBlockService(deleteItem.id);
                dispatch(deleteBlock(deleteItem.id));
                setSelectedBlock(undefined);
                setSelectedVillage(undefined);
                const blocks = await fetchBlocks(selectedState, selectedDistrict);
                dispatch(setBlocks(blocks));
            } else if (deleteItem.type === 'village') {
                await deleteVillageService(deleteItem.id);
                dispatch(deleteVillages(deleteItem.id));
                setSelectedVillage(undefined);
                if (selectedBlock) {
                    const villages = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
                    dispatch(setVillages(villages));
                }
            }
            setShowDeleteModal(false);
            setDeleteItem(null);
        } catch (err: any) {
            dispatch(setError(err.message || 'Failed to delete'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="my-auto mb-2">
                        <h3 className="page-title">District Admin Management</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    District Name
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {districtAdminDetails[0]?.relatedEntity?.districts?.name}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => {
                            if (activeTab === 'blocks') {
                                setEditBlock(undefined);
                                setShowBlockForm(true);
                            } else if (activeTab === 'villages' && selectedBlock) {
                                setEditVillage(undefined);
                                setShowVillageForm(true);
                            }
                        }}
                        disabled={activeTab === 'villages' && !selectedBlock}
                    >
                        {activeTab === 'blocks' ? 'Add Block' : 'Add Village'}
                    </Button>
                </div>

                {/* Dropdowns and Tabs */}
                <div className="row mb-3 align-items-end">
                    <div className="col-md-3">
                        <Form.Group>
                            <Form.Label>Select Block</Form.Label>
                            <Form.Select
                                value={selectedBlock || ''}
                                onChange={(e) => {
                                    setSelectedBlock(e.target.value ? Number(e.target.value) : undefined);
                                    setSelectedVillage(undefined);
                                    setActiveTab('blocks');
                                }}
                            >
                                <option value="">Select a block</option>
                                {Array.isArray(blocks) &&
                                    blocks
                                        .filter((block) => block.districtId === selectedDistrict)
                                        .map((block) => (
                                            <option key={block.id} value={block.id}>
                                                {block.name}
                                            </option>
                                        ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className="col-md-3">
                        <Form.Group>
                            <Form.Label>Select Village</Form.Label>
                            <Form.Select
                                value={selectedVillage || ''}
                                onChange={(e) => {
                                    setSelectedVillage(e.target.value ? Number(e.target.value) : undefined);
                                    setActiveTab('villages');
                                }}
                                disabled={!selectedBlock}
                            >
                                <option value="">Select a village</option>
                                {Array.isArray(villages) &&
                                    villages
                                        .filter((village) => village.blockAreaId === selectedBlock)
                                        .map((village) => (
                                            <option key={village.id} value={village.id}>
                                                {village.name}
                                            </option>
                                        ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className="col-md-4 d-flex mt-3">
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(key) => setActiveTab(key || 'blocks')}
                            id="location-tabs"
                            className="mb-0"
                        >
                            <Tab eventKey="blocks" title="Blocks" />
                            <Tab eventKey="villages" title="Villages" disabled={!selectedBlock} />
                        </Tabs>
                    </div>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body p-0 py-3">
                        {loading ? (
                            <div className="text-center py-5">
                                <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: '3rem' }}></i>
                                <h5 className="mt-3">Loading...</h5>
                            </div>
                        ) : activeTab === 'blocks' && filteredBlocks.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="mt-3 text-muted">No Blocks Found</h5>
                            </div>
                        ) : activeTab === 'villages' && filteredVillages.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="mt-3 text-muted">No Villages Found</h5>
                            </div>
                        ) : (
                            <Table
                                dataSource={activeTab === 'blocks' ? filteredBlocks : filteredVillages}
                                columns={activeTab === 'blocks' ? blockColumns : villageColumns}
                                Selection={false}
                                pagination={true}
                            />
                        )}
                    </div>
                </div>

                {/* Modals */}
                <BlockForm
                    show={showBlockForm}
                    onHide={() => setShowBlockForm(false)}
                    block={editBlock}
                    stateId={selectedState}
                    districtId={selectedDistrict}
                    onSuccess={() => setActiveTab('blocks')}

                />
                {selectedBlock && (
                    <VillageForm
                        show={showVillageForm}
                        onHide={() => setShowVillageForm(false)}
                        village={editVillage}
                        stateId={selectedState}
                        districtId={selectedDistrict}
                        blockAreaId={selectedBlock}
                    />
                )}

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this {deleteItem?.type}?
                    </Modal.Body>
                    <Modal.Footer className='gap-2'>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default DistrictAdmin;